import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/LogoForni.png';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { cajaBase } from '@/shared/lib/cajasEfectivo';
import type { ComprasFields } from '@/shared/api/controllers/compras';
import type { PagosComprasFields } from '@/shared/api/controllers/pagosCompras';
import type { CompraDetailResponse } from '@/features/compras/api';

/**
 * Generate a "Recibo de Pago" PDF — the non-AFIP receipt Fornitalia
 * emits to the proveedor after a pago is registered. Reuses the same
 * brand layout as the retention certificate (red header + logo) but
 * content is simpler: pago header + instrumentos list + retenciones
 * list + total.
 *
 * PowerApps uses a generic `HTML_PDF_Ver2` template for this (not
 * separately coded); we mirror it with autotable here.
 */

export type ReciboInput = {
  pago: { id: string; fields: PagosComprasFields };
  compra: { id: string; fields: ComprasFields };
  instrumentos: CompraDetailResponse['instrumentos'];
  retenciones: CompraDetailResponse['retenciones'];
  /**
   * Active country at generation time. Drives the issuer block in the
   * red header (FN FORNITALIA + RFC / Mexican address on MX) and the
   * footer block at the bottom of the page. Defaults to AR so older
   * callers behave unchanged.
   */
  country?: 'AR' | 'MX';
};

type Issuer = {
  name: string;
  /** Identifier line shown next to the name in the header (CUIT on AR,
   *  RFC + email on MX — we keep it short so it fits to the right of
   *  the title). */
  headerTag: string;
};

const ISSUER_AR: Issuer = {
  name: 'Fornitalia',
  headerTag: 'CUIT 30-71845033-7',
};

const ISSUER_MX: Issuer = {
  name: 'FN FORNITALIA',
  headerTag: 'mktfnfornitalia@gmail.com',
};

const BRAND_RED: [number, number, number] = [206, 44, 55];

export async function generateReciboPagoPDF(input: ReciboInput): Promise<Blob> {
  const country = input.country ?? 'AR';
  const issuer = country === 'MX' ? ISSUER_MX : ISSUER_AR;
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 12;

  // --- header band ---
  doc.setFillColor(...BRAND_RED);
  doc.rect(0, 0, pageWidth, 22, 'F');

  const logoDataUrl = await loadImageAsDataUrl(logoUrl);
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', margin, 4, 26, 10);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Recibo de Pago', pageWidth - margin, 10, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${issuer.name} · ${issuer.headerTag}`, pageWidth - margin, 16, {
    align: 'right',
  });

  doc.setTextColor(0, 0, 0);
  let y = 32;

  // --- pago header ---
  const pagoFields = input.pago.fields;
  const compraFields = input.compra.fields;
  const moneda: 'ARS' | 'MXN' | 'USD' =
    compraFields.Moneda_CO === 'Dolares'
      ? 'USD'
      : country === 'MX'
        ? 'MXN'
        : 'ARS';

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, font: 'helvetica', cellPadding: 2 },
    head: [['Recibo Nº', 'Fecha de pago', 'Proveedor']],
    headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
    body: [
      [
        pagoFields.Recibo_PC || input.pago.id,
        pagoFields.FechaPago_PC ?? '—',
        compraFields.Proveedor_CO ?? '—',
      ],
    ],
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, font: 'helvetica', cellPadding: 2 },
    head: [['Compra Nº', 'Comprobante', 'Fecha compra', 'Total compra']],
    headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
    body: [
      [
        input.compra.id,
        `${compraFields.Comprobante_CO ?? ''} ${compraFields.NumeroComprobante_CO ?? ''}`.trim(),
        compraFields.FechaComprobante_CO ?? '—',
        formatMoney(parseMoney(compraFields.Total_CO) ?? 0, moneda),
      ],
    ],
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // --- instrumentos ---
  if (input.instrumentos.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Instrumentos de pago', margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, font: 'helvetica', cellPadding: 2 },
      head: [['Cuenta', 'Tipo', 'Importe']],
      headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'right' } },
      body: input.instrumentos.map((i) => [
        // Documento que va al proveedor: nunca "Efectivo Pesos N".
        cajaBase(i.fields.Cuenta_IP ?? '—'),
        i.fields.TipoCuenta_IP ?? '—',
        formatMoney(parseMoney(i.fields.Importe_IP) ?? 0, moneda),
      ]),
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  }

  // --- retenciones ---
  if (input.retenciones.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Retenciones practicadas', margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, font: 'helvetica', cellPadding: 2 },
      head: [['Tipo', 'Concepto', 'Importe']],
      headStyles: { fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'right' } },
      body: input.retenciones.map((r) => [
        r.fields.Tipo_RP ?? '—',
        r.fields.Concepto_RP ?? '—',
        formatMoney(parseMoney(r.fields.ImporteRetenido_RP) ?? 0, 'ARS'),
      ]),
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  }

  // --- totals ---
  const importePagado = parseMoney(pagoFields.Importe_PC) ?? 0;
  const importeInstrumentos = parseMoney(pagoFields.ImporteInstrumentosPago_PC) ?? 0;
  const importeRetenciones = parseMoney(pagoFields.ImporteRetenciones_PC) ?? 0;
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: { fontSize: 10, font: 'helvetica', cellPadding: 3 },
    theme: 'plain',
    body: [
      [
        { content: 'Importe instrumentos:', styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatMoney(importeInstrumentos, moneda), styles: { halign: 'right' } },
      ],
      [
        { content: 'Importe retenciones:', styles: { halign: 'right', fontStyle: 'bold' } },
        // En la moneda de la compra, no en pesos: esta línea tiene que cerrar
        // con "Importe instrumentos" y "Total pago". El detalle de arriba sí
        // va en pesos — el certificado de retención se emite en pesos.
        { content: formatMoney(importeRetenciones, moneda), styles: { halign: 'right' } },
      ],
      [
        {
          content: 'Total pago:',
          styles: { halign: 'right', fontStyle: 'bold', fontSize: 12 },
        },
        {
          content: formatMoney(importePagado, moneda),
          styles: { halign: 'right', fontStyle: 'bold', fontSize: 12 },
        },
      ],
    ],
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // --- signature ---
  const pageHeight = doc.internal.pageSize.getHeight();
  const sigY = Math.max(y + 30, pageHeight - 40);
  doc.setDrawColor(100, 100, 100);
  doc.line(margin + 30, sigY, pageWidth - margin - 30, sigY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Firma y sello', pageWidth / 2, sigY + 5, { align: 'center' });

  // --- footer ---
  // On MX we anchor an FN FORNITALIA address block at the bottom of
  // the page (matches the cotización PDF treatment). AR keeps the
  // single-line "Generado el ..." stamp since its issuer info already
  // lives in the red header band.
  if (country === 'MX') {
    drawFooterMx(doc, pageWidth, pageHeight, margin);
  } else {
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Generado el ${new Date().toLocaleString('es-AR')}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' },
    );
  }

  return doc.output('blob');
}

/**
 * MX footer block — brand-red separator + business name, address,
 * phone and email. Mirrors the cotización PDF's MX footer so both
 * documents share the same Mexican fiscal data layout.
 */
function drawFooterMx(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  margin: number,
) {
  const lineHeight = 4;
  const lines = [
    { text: 'FN FORNITALIA', bold: true, size: 9 },
    {
      text:
        'LAGO MAYOR 116, 11320, ANAHUAC II SECCION, MIGUEL HIDALGO, CIUDAD DE MEXICO, CIUDAD DE MEXICO, México',
      bold: false,
      size: 7,
    },
    { text: '5539563525 · mktfnfornitalia@gmail.com', bold: false, size: 7 },
  ];
  const blockHeight = lines.length * lineHeight + 2;
  const baseY = pageHeight - blockHeight - 4;

  doc.setDrawColor(...BRAND_RED);
  doc.setLineWidth(0.5);
  doc.line(margin, baseY - 2, pageWidth - margin, baseY - 2);

  doc.setTextColor(0, 0, 0);
  lines.forEach((line, i) => {
    doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
    doc.setFontSize(line.size);
    doc.text(line.text, margin, baseY + (i + 1) * lineHeight);
  });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
}

export function reciboPagoFileName(pagoId: string): string {
  return `ReciboPago-${pagoId}.pdf`;
}

// ---------------------------------------------------------------------------
// Shared helpers (duplicated from certificadoRetencion for now — extract to
// a common module if we end up with more PDF generators).
// ---------------------------------------------------------------------------

let cachedLogoDataUrl: string | null = null;
async function loadImageAsDataUrl(url: string): Promise<string | null> {
  if (cachedLogoDataUrl) return cachedLogoDataUrl;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    cachedLogoDataUrl = dataUrl;
    return dataUrl;
  } catch {
    return null;
  }
}
